import * as React from 'react';
import { ACTION_VIEW_QUESTIONS_PROMPT } from 'utils/analytics/eventTypeConstantsAmplitude';
import { AnsweredQuestionsBanner } from 'components/business/receipts/pre-visit-questions/AnsweredQuestionsBanner';
import { Button, Typography } from 'omakase';
import { IconNote } from '@tock/icons';
import { LocalDateTime } from 'js-joda';
import {
  actionsContainer,
  headingWithAdornment,
  layoutContainer,
} from './previsitQuestionsCssDefs';
import { consumer, core } from '@tock/coretypes';
import { isEmpty } from 'es-toolkit/compat';
import { useAmplitudeSendEvent } from 'utils/analytics/AnalyticsContext';
import Anchor from 'components/Anchor';

interface PrevisitQuestionsProps {
  business?: consumer.IBusinessConfig | null;
  purchase: core.PastPurchase;
  questions: core.TicketTypeQuestion[] | null;
  ticketTypeDetails?: core.TicketTypeDetails | null;
  handleContinueToQuestions: () => void;
}

export const PrevisitQuestions: React.FunctionComponent<PrevisitQuestionsProps> = ({
  purchase,
  ticketTypeDetails,
  questions,
  handleContinueToQuestions,
}) => {
  const amplitudeSendEvent = useAmplitudeSendEvent();
  const shouldShowSurveyPreview =
    !isEmpty(questions) &&
    !ticketTypeDetails?.onlyShowInQuestionnaireEmail &&
    !purchase.isSurveyExpired &&
    !purchase.cancelledOrRefunded;
  const isUnanswered = purchase.question.length === 0;
  const isPastDate = LocalDateTime.parse(purchase.ticketDateTime).isBefore(LocalDateTime.now());

  /**
   * Track an event when a user has pre-visit questions to answer.
   * This will tell us how many guests are eligible to convert in
   * total and by config type.
   */
  React.useEffect(() => {
    if (shouldShowSurveyPreview && isUnanswered && !isPastDate) {
      const questionDisplayConfig = ticketTypeDetails?.shouldPromptSurvey ? 'URGENT' : 'DEFAULT';
      const purchaseToken = new URLSearchParams(window.location.search).get('token');
      const questionOrigin = purchaseToken ? 'EMAIL' : 'NAVIGATION';

      amplitudeSendEvent(ACTION_VIEW_QUESTIONS_PROMPT, { questionDisplayConfig, questionOrigin });
    }
  }, [shouldShowSurveyPreview, isUnanswered, isPastDate, ticketTypeDetails, amplitudeSendEvent]);

  if (!shouldShowSurveyPreview || isPastDate) {
    return null;
  }

  return isUnanswered ? (
    <div css={layoutContainer}>
      <div>
        <Typography variant="heading04" mb={1} css={headingWithAdornment}>
          <IconNote />
          Pre-visit questions
        </Typography>
        <Typography variant="body02">
          Help us ensure the best possible experience for you and your party.
        </Typography>
      </div>

      <div css={actionsContainer}>
        <Button
          component={Anchor}
          variant="primary"
          size="medium"
          fullWidth
          onClick={() => handleContinueToQuestions()}
          href="#"
          data-testid="answer-questions-button"
        >
          Answer questions
        </Button>
      </div>
    </div>
  ) : (
    <AnsweredQuestionsBanner purchase={purchase} />
  );
};
